<template lang="pug">
#resident-group-list(v-cloak)
  .box
    .box-header.d-flex.flex-row
      .titles
        .title
          span 利用者グループ情報一覧
        .note
          .error-message.p-0(v-if="errorMessage") {{ errorMessage }}
      .flex-fill.d-flex.justify-content-end
          button.btn.add-btn.mr-2(v-if="getRole != 2" @click="gotoResidentGroupRegister()") 追加
          button.btn.add-btn.mr-2.disable(v-else disabled) 追加
          button.btn.back-btn(@click="gotoResidentList()") 戻る
    .box-body
      .message
        strong 利用者グループ（例えばフロア単位）を作成し、所属利用者を設定できます。
        br
        span 利用者グループごとの面会スケジュール作成、一般ユーザーへの利用者グループごとの閲覧・操作権限付与が出来ます。
        br
        span 一般ユーザーは利用者グループの追加・修正・削除と所属利用者の変更ができません。管理者ユーザーが行ってください。
      ResidentGroupTable(
        v-model="groupItems"
        :fields="groupFields"
        :totalRows.sync="rows"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        @onEdit="gotoResidentGroupEdit"
        @onDelete="showConfirmDeleteModal"
        @onList="showResidentGroupMemberModal"
        @onMemberEdit="gotoResidentGroupMemberEdit"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center
        // ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(
            id="per-page-select"
            v-model="perPage"
            :options="pageOptions"
            size="md"
            @change="selectPerPage"
          )
        // ページネーション
        b-pagination.m-0(
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :disabled="isLoading"
          @change="selectPage"
          hide-goto-end-buttons
        )

  ResidentGroupDetailModal(v-model="modalGroupMembers" :group-name.sync="modalGroupName" ref="residentGroupDetailModal")
  OkCancelModal(v-model="showDeleteModal" :title="deleteMessage" @ok="deleteResidentGroup")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。" @ok="createView")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import { needRefreshesStore } from "@/store/store";
import { ResidentListState, SendMessagesState } from "@/store/NeedRefreshes";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import ResidentGroupMembersService, { ResidentGroupMembersGetModel } from "@/commons/services/ResidentGroupMembers";
import { Role } from "@/commons/services/Authorization";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ResidentGroupDetailModal from "@/pc/components/modals/ResidentGroupDetailModal.vue";
import ResidentGroupTable, { ResidentGroupTableField, ResidentGroupTableItem } from "@/pc/containers/residents/ResidentGroupTable.vue";
import SystemError from "@/commons/models/SystemError";

/**
 * 利用者グループ一覧画面コンポーネント
 */
@Component({
  components: {
    HelpIcon,
    PageLoading,
    OkCancelModal,
    OkModal,
    ResidentGroupDetailModal,
    ResidentGroupTable
  }
})
export default class ResidentGroupList extends Vue {
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** 利用者グループメンバーサービス */
  @Inject("ResidentGroupMembersService") residentGroupMembersService!: ResidentGroupMembersService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 削除確認モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** 利用者グループ列名 */
  groupFields: Array<ResidentGroupTableField> = [];
  /** 利用者グループ各行の情報 */
  groupItems: Array<ResidentGroupTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** 削除対象の利用者グループID */
  deleteTargetId: number = 0;
  /** 削除モーダルに表示するメッセージ */
  deleteMessage: string = "";

  /** モーダル表示対象の利用者 */
  modalGroupMembers: Array<ResidentGroupMembersGetModel> = [];
  /** モーダル表示対象の利用者グループ名 */
  modalGroupName: string = "";

  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.residentGroup.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.residentGroup.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.residentGroup.pageRange;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `resident-group-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### vue lifecycle mounted
   *
   * - 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const groupFields: Array<ResidentGroupTableField> = this.createGroupFields();
    this.groupFields = groupFields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.residentGroup.perPage);

    await this.createView();
    return;
  }

  /**
   * ### 利用者グループの列名を作成する
   */
  public createGroupFields(): Array<ResidentGroupTableField> {
    let fields: Array<ResidentGroupTableField> = [];

    fields.push({ key: "name", label: "グループ名", sortable: false, sortDirection: "desc", class: "group-name" });
    fields.push({ key: "operation", label: "登録情報", sortable: false, sortDirection: "desc", class: "text-center operation" });
    fields.push({ key: "members", label: "所属利用者", sortable: false, sortDirection: "desc", class: "text-center members" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["resident-group-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // 利用者テーブルに渡す各プロパティを作成する
    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const groupItems: Array<ResidentGroupTableItem> = this.createGroupItems(groups);
    this.groupItems = this.groupItems.concat(groupItems);
    this.currentRows = this.groupItems.length;

    this.isLoading = false;

    // 画面表示後、各行のグループにひもづく利用者数を取得する
    // DBのスループット上限問題、chromeのリクエスト上限を回避するために50件ずつ取得し、取得する毎に1秒インターバルを挟む
    let promises: Array<Promise<void>> = [];
    for (let i = 0; i < this.groupItems.length; i++) {
      let promise: Promise<void> = this.setGroupMemberCount(i, this.groupItems[i].id);
      promises.push(promise);

      if (Setting.requestLimit < promises.length) {
        const results: Array<any> = await Promise.all(promises);
        // 非同期処理中に例外が発生していればresultの中にSystemerrorが格納されているのでチェックする
        for (const result of results) {
          if (result instanceof SystemError) {
            errorHandle(result, this);
          }
        }
        promises = [];
        await this.$timerUtil.setTimeout(1000);
      }
    }
    let results: Array<any> = await Promise.all(promises);
    for (const result of results) {
      if (result instanceof SystemError) {
        errorHandle(result, this);
      }
    }

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const count: number = await this.residentGroupsService.getCount(userStore);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 利用者グループを作成する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const startIndex: number = this.currentRows;
    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const endIndex: number = this.perPage * (this.currentPage * this.pageRange);
    const params: any = {
      startIndex: startIndex,
      endIndex: endIndex,
      order: "asc"
    };

    const pageResidents: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore, params);
    return pageResidents;
  }

  /**
   * ### 利用者グループの行を作成する
   */
  public createGroupItems(groups: Array<ResidentGroupsGetModel>): Array<ResidentGroupTableItem> {
    let items: Array<ResidentGroupTableItem> = [];

    for (const group of groups) {
      items.push({
        id: group.id,
        name: group.name,
        memberCount: undefined,
        updated: group.updated
      });
    }

    return items;
  }

  /**
   * ### グループにひもづく利用者数を取得する
   */
  public setGroupMemberCount(no: number, groupId: number): Promise<void> {
    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const members: Array<ResidentGroupMembersGetModel> = await this.residentGroupMembersService.get(userStore, groupId);

        // 非同期処理中に画面遷移していた場合、このコンポーネントvueインスタンスが無くなっている可能性がある
        // 存在しない場合は何も処理しない
        if (this.groupItems != undefined && this.groupItems[no] != undefined) {
          this.groupItems[no].memberCount = members.length;
          this.$forceUpdate();
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.groupItems = [];
    this.currentRows = this.groupItems.length;
    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  /**
   * ### 利用者一覧画面へ遷移
   */
  public gotoResidentList(): void {
    this.$router.push(PcUrl.ResidentList);

    return;
  }

  /**
   * ### 利用者グループ登録画面へ遷移
   */
  public gotoResidentGroupRegister(): void {
    this.$router.push(PcUrl.ResidentGroupRegister);

    return;
  }

  /**
   * ### 利用者グループ編集画面へ遷移
   */
  public gotoResidentGroupEdit(group: ResidentGroupTableItem): void {
    this.$router.push({ name: PcUrl.ResidentGroupEdit, params: { id: `${group.id}` } });

    return;
  }

  /**
   * ### 利用者グループメンバー編集画面へ遷移
   */
  public gotoResidentGroupMemberEdit(group: ResidentGroupTableItem): void {
    this.$router.push({ name: PcUrl.ResidentGroupMemberEdit, params: { id: `${group.id}` } });

    return;
  }

  /**
   * ### 削除確認モーダルを表示する
   */
  public showConfirmDeleteModal(group: ResidentGroupTableItem): void {
    // 削除メッセージを動的に作成する
    const groupName: string = group.name ? group.name : "";
    this.deleteMessage = `${groupName}を削除しますか？`;
    this.showDeleteModal = true;

    this.deleteTargetId = group.id;

    return;
  }

  /**
   * ### 利用者グループを削除する
   */
  public async deleteResidentGroup(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    try {
      await this.residentGroupsService.delete(userStore, this.deleteTargetId);

      // 利用者一覧を再読み込みさせるようstoreを更新
      let residentList: ResidentListState = needRefreshesStore.residentList;
      residentList.residentList = true;
      needRefreshesStore.setResidentList(residentList);
      // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
      sendMessageState.sendMessages = true;
      needRefreshesStore.setSendMessage(sendMessageState);

      // 削除成功ダイアログを表示する
      this.showDeleteSuccessModal = true;
      this.isBusy = false;

      // ページに表示する利用者グループを取得しなおす
      this.groupItems = [];
      this.currentRows = this.groupItems.length;
      // 削除による画面の再描画もあるので、現在ページ番号はここで初期化する
      this.currentPage = 1;
      await this.createView();
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 400 && 0 < systemError.originalResponse.indexOf("residentGroup has residents")) {
        await this.createView();
        this.errorMessage = "利用者グループに所属する利用者が存在するため削除できません。所属する利用者の利用者グループを変更してから再度実行してください";
      } else if (systemError.statusCode == 400 && 0 < systemError.originalResponse.indexOf("There are rooms using residentGroup")) {
        await this.createView();
        this.errorMessage = "利用者グループを使用している部屋が存在するため削除できません。部屋の利用者グループを変更してから再度実行してください";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 利用者グループに所属する利用者をモーダルに表示する
   */
  public async showResidentGroupMemberModal(group: ResidentGroupTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const members: Array<ResidentGroupMembersGetModel> = await this.residentGroupMembersService.get(userStore, group.id);
    this.modalGroupMembers = members;
    this.modalGroupName = group.name;

    const residentGroupDetailModal: ResidentGroupDetailModal | Vue | Element | Vue[] | Element[] = this.$refs.residentGroupDetailModal;
    if (residentGroupDetailModal instanceof ResidentGroupDetailModal) {
      residentGroupDetailModal.show();
    }

    this.isBusy = false;

    return;
  }

  /** 権限ロールを返却する */
  get getRole(): Role {
    return userStore.loginUser.role;
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#resident-group-list {
  max-width: 980px;
  margin-left: auto;
  margin-right: auto;

  .title {
    font-size: 22px;
    font-weight: bold;
  }
  .add-btn,
  .back-btn {
    width: 80px;
  }
}
</style>
